import { logDataModels } from '../dataModelsLogger';
import type { PolicyAsset, ExpressionIssue, ValidationResult } from './types';
import { resolveColumnRef } from './usePolicyAssetColumns';
import { FUNCTION_REGISTRY, isKnownVariable, variableByPath } from './variables';

type TokenKind =
  | 'ident'
  | 'number'
  | 'string'
  | 'variable'
  | 'op'
  | 'lparen'
  | 'rparen'
  | 'comma'
  | 'eof';

interface Token {
  kind: TokenKind;
  value: string;
  from: number;
  to: number;
}

export type AstNode =
  | { type: 'binary'; op: string; left: AstNode; right: AstNode; from: number; to: number }
  | { type: 'unary'; op: string; expr: AstNode; from: number; to: number }
  | { type: 'in'; not: boolean; left: AstNode; values: AstNode; from: number; to: number }
  | { type: 'isnull'; not: boolean; expr: AstNode; from: number; to: number }
  | { type: 'call'; name: string; args: AstNode[]; from: number; to: number }
  | { type: 'column'; name: string; from: number; to: number }
  | { type: 'variable'; path: string; from: number; to: number }
  | { type: 'literal'; value: string | number | boolean | null; valueType: string; from: number; to: number }
  | { type: 'list'; items: AstNode[]; from: number; to: number }
  | { type: 'subquery'; sql: string; from: number; to: number }
  | { type: 'exists'; not: boolean; subquery: AstNode; from: number; to: number }
  | { type: 'case'; branches: Array<{ when: AstNode; then: AstNode }>; else?: AstNode; from: number; to: number };

const MASK_SQL_FUNCTIONS = new Set(['CAST', 'REGEXP_REPLACE', 'REPLACE', 'CONCAT']);

export function looksLikeMaskCase(source: string): boolean {
  const text = source.trim();
  if (!text) return false;
  if (!/^\s*CASE\b/i.test(text)) return false;
  return /\bVALUE\b/i.test(text) || /\bTHEN\b/i.test(text);
}

const OPS = ['!=', '>=', '<=', '=', '>', '<'];
const FUNCTIONS = new Set(FUNCTION_REGISTRY.map((item) => item.name));

function tokenize(source: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  const push = (kind: TokenKind, value: string, from: number, to: number) => tokens.push({ kind, value, from, to });
  while (i < source.length) {
    const ch = source[i];
    if (/\s/.test(ch)) {
      i += 1;
      continue;
    }
    if (source.startsWith('${', i)) {
      const end = source.indexOf('}', i + 2);
      if (end < 0) {
        push('variable', source.slice(i + 2), i, source.length);
        i = source.length;
        continue;
      }
      push('variable', source.slice(i + 2, end), i, end + 1);
      i = end + 1;
      continue;
    }
    if (ch === "'" || ch === '"') {
      const quote = ch;
      let j = i + 1;
      let value = '';
      while (j < source.length && source[j] !== quote) {
        value += source[j];
        j += 1;
      }
      push('string', value, i, Math.min(j + 1, source.length));
      i = j < source.length ? j + 1 : j;
      continue;
    }
    if (/[0-9]/.test(ch) || (ch === '-' && /[0-9]/.test(source[i + 1] ?? ''))) {
      let j = i + 1;
      while (j < source.length && /[0-9.]/.test(source[j])) j += 1;
      push('number', source.slice(i, j), i, j);
      i = j;
      continue;
    }
    const two = source.slice(i, i + 2);
    if (OPS.includes(two)) {
      push('op', two, i, i + 2);
      i += 2;
      continue;
    }
    if (OPS.includes(ch) || ch === '!') {
      push('op', ch, i, i + 1);
      i += 1;
      continue;
    }
    if (ch === '(') {
      push('lparen', '(', i, i + 1);
      i += 1;
      continue;
    }
    if (ch === ')') {
      push('rparen', ')', i, i + 1);
      i += 1;
      continue;
    }
    if (ch === ',') {
      push('comma', ',', i, i + 1);
      i += 1;
      continue;
    }
    if (/[A-Za-z_]/.test(ch)) {
      let j = i + 1;
      while (j < source.length && /[A-Za-z0-9_.]/.test(source[j])) j += 1;
      push('ident', source.slice(i, j), i, j);
      i = j;
      continue;
    }
    push('ident', ch, i, i + 1);
    i += 1;
  }
  push('eof', '', source.length, source.length);
  return tokens;
}

class Parser {
  private index = 0;
  constructor(private tokens: Token[], readonly source: string) {}

  peek(): Token {
    return this.tokens[this.index];
  }

  take(): Token {
    const token = this.tokens[this.index];
    this.index += 1;
    return token;
  }

  matchIdent(...values: string[]): Token | null {
    const token = this.peek();
    if (token.kind === 'ident' && values.some((value) => value.toLowerCase() === token.value.toLowerCase())) {
      return this.take();
    }
    return null;
  }

  parse(): AstNode {
    const node = this.parseOr();
    if (this.peek().kind !== 'eof') {
      throw this.error(`Unexpected token '${this.peek().value}'`, this.peek());
    }
    return node;
  }

  private parseOr(): AstNode {
    let left = this.parseAnd();
    while (this.matchIdent('OR')) {
      const right = this.parseAnd();
      left = { type: 'binary', op: 'OR', left, right, from: left.from, to: right.to };
    }
    return left;
  }

  private parseAnd(): AstNode {
    let left = this.parseNot();
    while (this.matchIdent('AND')) {
      const right = this.parseNot();
      left = { type: 'binary', op: 'AND', left, right, from: left.from, to: right.to };
    }
    return left;
  }

  private parseNot(): AstNode {
    const token = this.matchIdent('NOT');
    if (token) {
      const expr = this.parseNot();
      return { type: 'unary', op: 'NOT', expr, from: token.from, to: expr.to };
    }
    return this.parseComparison();
  }

  private parseComparison(): AstNode {
    const left = this.parsePrimary();
    if (this.matchIdent('IS')) {
      const not = Boolean(this.matchIdent('NOT'));
      const nul = this.matchIdent('NULL');
      if (!nul) throw this.error('Expected NULL after IS', this.peek());
      return { type: 'isnull', not, expr: left, from: left.from, to: nul.to };
    }
    if (this.matchIdent('NOT') && this.matchIdent('IN')) {
      const values = this.parseInRhs();
      return { type: 'in', not: true, left, values, from: left.from, to: values.to };
    }
    if (this.matchIdent('IN')) {
      const values = this.parseInRhs();
      return { type: 'in', not: false, left, values, from: left.from, to: values.to };
    }
    if (this.peek().kind === 'op') {
      const op = this.take();
      const right = this.parsePrimary();
      return { type: 'binary', op: op.value, left, right, from: left.from, to: right.to };
    }
    return left;
  }

  private tokenAt(offset: number): Token {
    return this.tokens[Math.min(this.index + offset, this.tokens.length - 1)];
  }

  private isSelectSubquery(): boolean {
    return this.peek().kind === 'lparen'
      && this.tokenAt(1).kind === 'ident'
      && this.tokenAt(1).value.toUpperCase() === 'SELECT';
  }

  private parseInRhs(): AstNode {
    if (this.isSelectSubquery()) return this.parseSelectSubquery();
    return this.parsePrimary();
  }

  private parseSelectSubquery(): AstNode {
    const open = this.take();
    let depth = 1;
    let close = open;
    while (depth > 0 && this.peek().kind !== 'eof') {
      close = this.take();
      if (close.kind === 'lparen') depth += 1;
      else if (close.kind === 'rparen') depth -= 1;
    }
    if (depth !== 0 || close.kind !== 'rparen') throw this.error('Missing closing parenthesis', open);
    return { type: 'subquery', sql: this.source.slice(open.from, close.to), from: open.from, to: close.to };
  }

  private parseCase(): AstNode {
    const start = this.take();
    const branches: Array<{ when: AstNode; then: AstNode }> = [];
    while (this.matchIdent('WHEN')) {
      const when = this.parseOr();
      if (!this.matchIdent('THEN')) throw this.error('Expected THEN after WHEN', this.peek());
      const then = this.parseOr();
      branches.push({ when, then });
    }
    if (!branches.length) throw this.error('Expected WHEN after CASE', this.peek());
    let elseNode: AstNode | undefined;
    if (this.matchIdent('ELSE')) elseNode = this.parseOr();
    const end = this.matchIdent('END');
    if (!end) throw this.error('Expected END to close CASE', this.peek());
    return { type: 'case', branches, else: elseNode, from: start.from, to: end.to };
  }

  private parseCast(): AstNode {
    const start = this.take();
    if (this.peek().kind !== 'lparen') throw this.error('Expected ( after CAST', this.peek());
    this.take();
    const expr = this.parseOr();
    if (!this.matchIdent('AS')) throw this.error('Expected AS in CAST', this.peek());
    const typeToken = this.peek();
    if (typeToken.kind !== 'ident') throw this.error('Expected type after AS', typeToken);
    this.take();
    if (this.peek().kind === 'lparen') {
      this.take();
      while (this.peek().kind !== 'rparen' && this.peek().kind !== 'eof') this.take();
      if (this.peek().kind !== 'rparen') throw this.error('Missing closing parenthesis', start);
      this.take();
    }
    if (this.peek().kind !== 'rparen') throw this.error('Missing closing parenthesis for CAST', start);
    const close = this.take();
    return {
      type: 'call',
      name: 'CAST',
      args: [expr, { type: 'literal', value: typeToken.value, valueType: 'STRING', from: typeToken.from, to: typeToken.to }],
      from: start.from,
      to: close.to,
    };
  }

  private parsePrimary(): AstNode {
    const token = this.peek();
    if (token.kind === 'ident' && token.value.toUpperCase() === 'CASE') return this.parseCase();
    if (token.kind === 'ident' && token.value.toUpperCase() === 'CAST' && this.tokenAt(1).kind === 'lparen') {
      return this.parseCast();
    }
    if (token.kind === 'lparen') {
      const open = this.take();
      const items: AstNode[] = [this.parseOr()];
      while (this.peek().kind === 'comma') {
        this.take();
        items.push(this.parseOr());
      }
      if (this.peek().kind !== 'rparen') throw this.error('Missing closing parenthesis', open);
      const close = this.take();
      if (items.length === 1) return { ...items[0], from: open.from, to: close.to };
      return { type: 'list', items, from: open.from, to: close.to };
    }
    if (token.kind === 'variable') {
      this.take();
      return { type: 'variable', path: token.value, from: token.from, to: token.to };
    }
    if (token.kind === 'string') {
      this.take();
      return { type: 'literal', value: token.value, valueType: looksLikeDate(token.value) ? 'TIMESTAMP' : 'STRING', from: token.from, to: token.to };
    }
    if (token.kind === 'number') {
      this.take();
      return { type: 'literal', value: Number(token.value), valueType: 'NUMBER', from: token.from, to: token.to };
    }
    if (token.kind === 'ident') {
      const ident = this.take();
      if (ident.value.toUpperCase() === 'TRUE' || ident.value.toUpperCase() === 'FALSE') {
        return { type: 'literal', value: ident.value.toUpperCase() === 'TRUE', valueType: 'BOOLEAN', from: ident.from, to: ident.to };
      }
      if (ident.value.toUpperCase() === 'NULL') {
        return { type: 'literal', value: null, valueType: 'NULL', from: ident.from, to: ident.to };
      }
      if (ident.value.toUpperCase() === 'CURRENT_DATE' || ident.value.toUpperCase() === 'CURRENT_TIMESTAMP') {
        return { type: 'call', name: ident.value.toUpperCase(), args: [], from: ident.from, to: ident.to };
      }
      if (ident.value.toUpperCase() === 'EXISTS' && this.isSelectSubquery()) {
        const subquery = this.parseSelectSubquery();
        return { type: 'exists', not: false, subquery, from: ident.from, to: subquery.to };
      }
      if (this.peek().kind === 'lparen') {
        this.take();
        const args: AstNode[] = [];
        if (this.peek().kind !== 'rparen') {
          args.push(this.parseOr());
          while (this.peek().kind === 'comma') {
            this.take();
            args.push(this.parseOr());
          }
        }
        if (this.peek().kind !== 'rparen') throw this.error(`Missing closing parenthesis for ${ident.value}`, ident);
        const close = this.take();
        return { type: 'call', name: ident.value.toUpperCase(), args, from: ident.from, to: close.to };
      }
      return { type: 'column', name: ident.value, from: ident.from, to: ident.to };
    }
    throw this.error('Invalid syntax', token);
  }

  private error(message: string, token: Token): Error {
    const error = new Error(message);
    (error as Error & { from: number; to: number }).from = token.from;
    (error as Error & { from: number; to: number }).to = token.to;
    return error;
  }
}

function looksLikeDate(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}(T[\d:.]+Z?)?$/.test(value);
}

export function parseExpression(source: string): { ast?: AstNode; issues: ExpressionIssue[] } {
  const trimmed = source.trim();
  if (!trimmed) return { issues: [{ message: 'Expression is empty', from: 0, to: 0, severity: 'error' }] };
  try {
    const ast = new Parser(tokenize(trimmed), trimmed).parse();
    return { ast, issues: [] };
  } catch (error) {
    const err = error as Error & { from?: number; to?: number };
    logDataModels('warning', 'governance_expression_parse_failed', { message: err.message });
    return {
      issues: [{ message: err.message || 'Invalid syntax', from: err.from ?? 0, to: err.to ?? trimmed.length, severity: 'error' }],
    };
  }
}

function columnType(name: string, assets: PolicyAsset[]): string | undefined {
  return resolveColumnRef(name, assets)[0]?.column.type;
}

function isQualifiedColumnRef(name: string): boolean {
  return name.includes('.');
}

function normalizeType(type?: string): string {
  if (!type) return 'UNKNOWN';
  const upper = type.toUpperCase();
  if (['BIGINT', 'INTEGER', 'INT', 'NUMBER', 'DECIMAL', 'FLOAT'].includes(upper)) return 'NUMBER';
  if (['VARCHAR', 'STRING', 'TEXT'].includes(upper)) return 'STRING';
  if (['DATE', 'TIMESTAMP'].includes(upper)) return 'TIMESTAMP';
  if (upper === 'BOOLEAN') return 'BOOLEAN';
  if (upper.endsWith('[]')) return 'STRING[]';
  return upper;
}

function inferNodeType(node: AstNode, assets: PolicyAsset[]): string {
  if (node.type === 'literal') return node.valueType;
  if (node.type === 'variable') return variableByPath(node.path)?.type ?? 'UNKNOWN';
  if (node.type === 'column') return normalizeType(columnType(node.name, assets));
  if (node.type === 'list' || node.type === 'subquery') return 'LIST';
  if (node.type === 'call') {
    if (node.name === 'CURRENT_DATE' || node.name === 'CURRENT_TIMESTAMP') return 'TIMESTAMP';
    return 'STRING';
  }
  return 'BOOLEAN';
}

const SUBQUERY_TOKEN_RE = /\$\{([A-Za-z_][A-Za-z0-9_.]*)\}/g;

function walkSubquery(node: Extract<AstNode, { type: 'subquery' }>, issues: ExpressionIssue[]): void {
  issues.push({
    message: 'Subquery is stored as SQL and applied at query time. Test query cannot simulate it.',
    from: node.from,
    to: node.to,
    severity: 'warning',
  });
  SUBQUERY_TOKEN_RE.lastIndex = 0;
  let match: RegExpExecArray | null = SUBQUERY_TOKEN_RE.exec(node.sql);
  while (match) {
    const path = match[1];
    if (!isKnownVariable(path)) {
      issues.push({
        message: `Unknown variable \${${path}}`,
        from: node.from + match.index,
        to: node.from + match.index + match[0].length,
        severity: 'error',
      });
    }
    match = SUBQUERY_TOKEN_RE.exec(node.sql);
  }
}

export function validateExpression(source: string, assets: PolicyAsset[]): ValidationResult {
  const { ast, issues } = parseExpression(source);
  if (!ast) {
    return { ok: false, errors: issues.map((issue) => issue.message), issues, unsupported: false };
  }
  walk(ast, assets, issues);
  const unsupported = issues.some((issue) => issue.unsupported);
  const errors = issues.filter((issue) => issue.severity === 'error').map((issue) => issue.message);
  return { ok: errors.length === 0 && !unsupported, errors, issues, unsupported };
}

function walk(node: AstNode, assets: PolicyAsset[], issues: ExpressionIssue[]): void {
  if (node.type === 'variable') {
    if (!isKnownVariable(node.path)) {
      issues.push({ message: `Unknown variable \${${node.path}}`, from: node.from, to: node.to, severity: 'error' });
    }
    return;
  }
  if (node.type === 'case') {
    node.branches.forEach((branch) => {
      walk(branch.when, assets, issues);
      walk(branch.then, assets, issues);
    });
    if (node.else) walk(node.else, assets, issues);
    return;
  }
  if (node.type === 'column') {
    if (node.name.toLowerCase() === 'value') return;
    const matches = resolveColumnRef(node.name, assets);
    const hasSchema = assets.some((asset) => asset.columns.length);
    if (!matches.length) {
      if (hasSchema) {
        issues.push({ message: `Unknown column '${node.name}'`, from: node.from, to: node.to, severity: 'error' });
      }
    } else if (!isQualifiedColumnRef(node.name) && matches.length < assets.length && assets.length > 1) {
      const missing = assets.filter((asset) => !matches.some((match) => match.asset.id === asset.id)).map((asset) => asset.name);
      issues.push({
        message: `Column '${node.name}' is missing from ${missing.join(', ')}. Use the full path to target one table.`,
        from: node.from,
        to: node.to,
        severity: 'warning',
      });
    }
    return;
  }
  if (node.type === 'call') {
    if (!FUNCTIONS.has(node.name) && !MASK_SQL_FUNCTIONS.has(node.name)) {
      issues.push({ message: `Unsupported function ${node.name}()`, from: node.from, to: node.to, severity: 'error', unsupported: true });
    } else if (MASK_SQL_FUNCTIONS.has(node.name) && !FUNCTIONS.has(node.name)) {
      issues.push({
        message: `${node.name}() is applied at query time. Test query cannot simulate the rewritten cell.`,
        from: node.from,
        to: node.to,
        severity: 'warning',
      });
    }
    node.args.forEach((arg) => walk(arg, assets, issues));
    return;
  }
  if (node.type === 'isnull') {
    walk(node.expr, assets, issues);
    return;
  }
  if (node.type === 'in') {
    walk(node.left, assets, issues);
    walk(node.values, assets, issues);
    if (node.values.type === 'subquery') return;
    const leftType = inferNodeType(node.left, assets);
    const rightType = inferNodeType(node.values, assets);
    if (rightType !== 'LIST' && rightType !== 'STRING[]' && node.values.type !== 'variable') {
      issues.push({ message: 'IN requires a list or array variable', from: node.from, to: node.to, severity: 'error' });
    }
    if (leftType === 'NUMBER' && rightType === 'STRING') {
      issues.push({ message: 'Incorrect operand types for IN', from: node.from, to: node.to, severity: 'error' });
    }
    return;
  }
  if (node.type === 'subquery') {
    walkSubquery(node, issues);
    return;
  }
  if (node.type === 'exists') {
    walk(node.subquery, assets, issues);
    return;
  }
  if (node.type === 'unary') {
    walk(node.expr, assets, issues);
    return;
  }
  if (node.type === 'binary') {
    walk(node.left, assets, issues);
    walk(node.right, assets, issues);
    if (['>', '<', '>=', '<='].includes(node.op)) {
      const left = inferNodeType(node.left, assets);
      const right = inferNodeType(node.right, assets);
      if (left === 'STRING' && right === 'NUMBER') {
        issues.push({ message: `Operator ${node.op} is incompatible with STRING and NUMBER`, from: node.from, to: node.to, severity: 'error' });
      }
    }
    return;
  }
  if (node.type === 'list') node.items.forEach((item) => walk(item, assets, issues));
}

export function evaluateBoolean(node: AstNode, ctx: Record<string, unknown>): boolean | 'unsupported' {
  const value = evaluateValue(node, ctx);
  if (value === 'unsupported') return 'unsupported';
  return Boolean(value);
}

export function evaluateValue(node: AstNode, ctx: Record<string, unknown>): unknown {
  switch (node.type) {
    case 'literal':
      return node.value;
    case 'column': {
      if (Object.prototype.hasOwnProperty.call(ctx, node.name)) return ctx[node.name];
      const bare = node.name.includes('.') ? node.name.slice(node.name.lastIndexOf('.') + 1) : node.name;
      return ctx[bare];
    }
    case 'variable':
      return ctx[node.path];
    case 'list':
      return node.items.map((item) => evaluateValue(item, ctx));
    case 'isnull': {
      const value = evaluateValue(node.expr, ctx);
      return node.not ? value != null : value == null;
    }
    case 'unary': {
      const value = evaluateBoolean(node.expr, ctx);
      if (value === 'unsupported') return 'unsupported';
      return !value;
    }
    case 'in': {
      if (node.values.type === 'subquery') return 'unsupported';
      const left = evaluateValue(node.left, ctx);
      const right = evaluateValue(node.values, ctx);
      const list = Array.isArray(right) ? right : [right];
      const hit = list.map(String).includes(String(left));
      return node.not ? !hit : hit;
    }
    case 'case': {
      for (const branch of node.branches) {
        const matched = evaluateBoolean(branch.when, ctx);
        if (matched === 'unsupported') return 'unsupported';
        if (matched) return evaluateValue(branch.then, ctx);
      }
      return node.else ? evaluateValue(node.else, ctx) : null;
    }
    case 'subquery':
    case 'exists':
      return 'unsupported';
    case 'binary': {
      if (node.op === 'AND' || node.op === 'OR') {
        const left = evaluateBoolean(node.left, ctx);
        const right = evaluateBoolean(node.right, ctx);
        if (left === 'unsupported' || right === 'unsupported') return 'unsupported';
        return node.op === 'AND' ? left && right : left || right;
      }
      const left = evaluateValue(node.left, ctx);
      const right = evaluateValue(node.right, ctx);
      if (node.op === '=') return String(left) === String(right);
      if (node.op === '!=' || node.op === '<>') return String(left) !== String(right);
      const ln = Number(left);
      const rn = Number(right);
      if (['>', '<', '>=', '<='].includes(node.op) && (Number.isNaN(ln) || Number.isNaN(rn))) {
        const ls = String(left);
        const rs = String(right);
        if (node.op === '>') return ls > rs;
        if (node.op === '<') return ls < rs;
        if (node.op === '>=') return ls >= rs;
        return ls <= rs;
      }
      if (node.op === '>') return ln > rn;
      if (node.op === '<') return ln < rn;
      if (node.op === '>=') return ln >= rn;
      if (node.op === '<=') return ln <= rn;
      return 'unsupported';
    }
    case 'call': {
      const args = node.args.map((arg) => evaluateValue(arg, ctx));
      if (args.includes('unsupported')) return 'unsupported';
      if (node.name === 'LOWER') return String(args[0] ?? '').toLowerCase();
      if (node.name === 'UPPER') return String(args[0] ?? '').toUpperCase();
      if (node.name === 'COALESCE') return args.find((arg) => arg != null) ?? null;
      if (node.name === 'STARTS_WITH') return String(args[0] ?? '').startsWith(String(args[1] ?? ''));
      if (node.name === 'ENDS_WITH') return String(args[0] ?? '').endsWith(String(args[1] ?? ''));
      if (node.name === 'CONTAINS') return String(args[0] ?? '').includes(String(args[1] ?? ''));
      if (node.name === 'CURRENT_DATE') return new Date().toISOString().slice(0, 10);
      if (node.name === 'CURRENT_TIMESTAMP') return new Date().toISOString();
      return 'unsupported';
    }
    default:
      return 'unsupported';
  }
}

export function conditionToSql(node: import('./types').ConditionNode): string {
  if (node.kind === 'group') {
    const inner = node.children.map(conditionToSql).filter(Boolean).join(` ${node.combinator} `);
    return inner ? `(${inner})` : '';
  }
  const left = node.left.kind === 'variable' ? `\${${node.left.name}}` : node.left.name;
  if (node.operator === 'is_null') return `${left} IS NULL`;
  if (node.operator === 'is_not_null') return `${left} IS NOT NULL`;
  const op = {
    eq: '=',
    neq: '!=',
    gt: '>',
    lt: '<',
    gte: '>=',
    lte: '<=',
    in: 'IN',
    not_in: 'NOT IN',
    contains: 'CONTAINS',
    contains_any: 'CONTAINS ANY',
  }[node.operator];
  if (!node.right) return left;
  const right = formatRight(node.right);
  if (node.operator === 'contains') return `CONTAINS(${left}, ${right})`;
  return `${left} ${op} ${right}`;
}

function formatRight(right: { kind: string; value: string }): string {
  if (right.kind === 'user_var' || right.kind === 'request_var' || right.kind === 'resource_var') return `\${${right.value}}`;
  if (right.kind === 'column') return right.value;
  if (right.value.includes(',') && !right.value.startsWith('(')) {
    return `(${right.value.split(',').map((part) => `'${part.trim()}'`).join(', ')})`;
  }
  if (/^-?\d+(\.\d+)?$/.test(right.value)) return right.value;
  if (right.value.startsWith("'") || right.value.startsWith('(') || right.value.startsWith('${')) return right.value;
  return `'${right.value}'`;
}
